import { useQuery } from '@tanstack/react-query';
import { set } from 'lodash';
import { DocumentIdProps } from 'models/general';
import { WorklistData } from 'models/worklist';
import { EmptyParametersError } from 'services/errors/EmptyParametersError';
import { getUsers } from 'services/user';
import { API_FUNCTIONS, queryKeys, RULES_ENDPOINTS, sdk } from 'utils/query';

export const getWorklistRecord = async (variables: DocumentIdProps) => {
  const { documentId } = variables;
  if (!documentId) throw new EmptyParametersError();

  const result = await sdk.tasks.api.get<WorklistData>(
    API_FUNCTIONS.monitoring,
    `${RULES_ENDPOINTS.monitoringNotifications}/${documentId}`,
    {},
  );

  if (result?.lastInteraction) {
    const users = await getUsers([result.lastInteraction.userId], 1);
    const interactionUser = users.data.find((user) => user.id === result.lastInteraction?.userId);
    if (!interactionUser) return result;
    set(result, 'lastInteraction.userName', `${interactionUser?.firstName} ${interactionUser?.lastName} `);
  }

  if (result?.notes && result.notes.length > 0) {
    const { notes } = result;
    const notesUserIds = notes.map((note) => note.updaterId);
    const users = await getUsers(notesUserIds, 50);

    const notesWithNames = notes.map((note) => {
      const noteUser = users.data.find((user) => user.id === note.updaterId);
      return { ...note, userName: `${noteUser?.lastName} ${noteUser?.firstName}` };
    });
    return { ...result, notes: notesWithNames };
  }

  return result;
};

export const useGetWorklistRecord = (variables: DocumentIdProps) =>
  useQuery({
    queryKey: [queryKeys.worklistRecord, variables.documentId],
    queryFn: () => getWorklistRecord(variables),
    enabled: !!variables.documentId,
  });
